// 待宵の花嫁「ヴェールの招待」(veilInvitation・床の帯 kind 'veil') の見た目 — 布のヴェール (2026-10-23・比較版)。
//
// 作者の原文 (10-20 音声入力): 「やっぱり腕じゃなくて、もっとベールっぽくしたいです。…攻撃のヒット判定の後も、
//   波打つように動くようなエフェクトにしてほしいです」
// 段:
//   ① 予告 (850ms): 帯の中の床に裾が溜まってさざめく (高さ 10〜26px)。最後の 25% で約 40px まで持ち上がってはためく。
//   ② 当たり 0〜110ms: 薄い布が床から一気に立ち上がる (少し行き過ぎて戻る)。上の縁は波打ったまま。
//   ③ 当たりの間: 立ったまま波打つ。床下の菫の照り (弾けない合図) が満ちる。
//   ④ 当たりの終わり〜約 900ms: 照りはすぐ引く。布は波打ったまま垂れ、床から少し浮き、上から薄れて消える。
// 2案 (?brideVeilFx=cloth / cloth2・DEV だけ):
//   cloth  = 薄絹1枚。細かいさざ波が速く横へ流れる。
//   cloth2 = 二枚重ね。大きくゆったりうねり、奥の1枚が遅れて追う。
// 布は1枚の多角形で描く (上の縁が波・下は床)。襞は細い紡錘形の濃淡を重ねる。白い線・記号の列は描かない。
// 描くだけ。当たり (場所・時間・ダメージ) は変えない。描く点は全部 帯の ±V_BLEED の中。
// 色: 菫は花嫁の弾けない合図 (影の手・前の壁の根元と同じ swc('unparry', …))。布の地は淡い銀藤。
import type { ActiveHit } from './types'
import { hash01 } from './fx_kit'
import { sw as swc } from './purple_swap'

export type VeilClothKind = 'cloth' | 'cloth2'

function clamp01(v: number): number { return v < 0 ? 0 : v > 1 ? 1 : v }
function smooth01(a: number, b: number, v: number): number {
  const p = clamp01((v - a) / (b - a))
  return p * p * (3 - 2 * p)
}
function easeOut(p: number): number { const q = 1 - clamp01(p); return 1 - q * q * q }
// 行き過ぎて戻る (最大 約1.08)
function easeOutBack(p: number): number {
  const x = clamp01(p) - 1
  const c = 1.5
  return 1 + (c + 1) * x * x * x + c * x * x
}

const V_BLEED = 8            // 帯の左右へはみ出してよい幅
const RISE_MS = 110          // 当たりの 0ms から立ち上がるまで
export const CLOTH_LINGER_MS = 900   // 当たりの後、垂れて消えるまで
const STEP_PX = 5            // 縁の点の間隔

// 案ごとの揺れ方。amp=縁の上下 (px)・len=波長 (px)・spd=流れる速さ (rad/s)・sway=上の縁の横の揺れ (px)。
type Sheet = { amp: number; len: number; spd: number; amp2: number; len2: number; spd2: number; sway: number; swaySpd: number; hMul: number; alpha: number; tint: string; lag: number }
const SHEETS: Record<VeilClothKind, Sheet[]> = {
  cloth: [
    { amp: 6, len: 54, spd: 9.5, amp2: 2.5, len2: 21, spd2: -13, sway: 5, swaySpd: 6.2, hMul: 1, alpha: 1, tint: '214,202,240', lag: 0 },
  ],
  cloth2: [
    // 奥の1枚 (少し高く・濃く・遅れて追う)
    { amp: 15, len: 170, spd: 2.6, amp2: 4, len2: 62, spd2: -3.4, sway: 12, swaySpd: 2.1, hMul: 1.1, alpha: 0.6, tint: '176,150,222', lag: 0.5 },
    // 手前の1枚
    { amp: 17, len: 140, spd: 3.2, amp2: 5, len2: 55, spd2: -4.1, sway: 14, swaySpd: 2.6, hMul: 0.9, alpha: 0.9, tint: '218,206,242', lag: 0 },
  ],
}

type Geom = { xStart: number; xEnd: number; w: number; cx: number; seed: number; fullH: number }
function geomOf(hz: ActiveHit): Geom {
  const w = hz.xEnd - hz.xStart
  return { xStart: hz.xStart, xEnd: hz.xEnd, w, cx: (hz.xStart + hz.xEnd) / 2, seed: hz.id * 1.91 + 23.7, fullH: Math.min(200, 140 + w * 0.2) }
}

// 帯の端ほど低い (裾の角が垂れる)。u=0..1。
function profile(u: number): number { return 0.3 + 0.7 * Math.pow(Math.sin(Math.PI * clamp01(u)), 0.55) }

type Pose = { h: number; lift: number; fadeTop: number; fadeAll: number; calm: number }

// 布1枚を描く。h=立ち上がった高さ (px)・lift=床から浮いた量・fadeTop=上から薄れる (0..1)・fadeAll=全体の薄れ。
function drawSheet(ctx: CanvasRenderingContext2D, g: Geom, sh: Sheet, floorY: number, pose: Pose, timeSec: number): void {
  const h = pose.h * sh.hMul
  if (h < 2) return
  const a = sh.alpha * (1 - pose.fadeAll)
  if (a <= 0.01) return
  const t = timeSec - sh.lag * 0.3
  const x0 = g.xStart - 2, x1 = g.xEnd + 2
  const n = Math.max(4, Math.ceil((x1 - x0) / STEP_PX))
  const bottom = floorY + 2 - pose.lift
  // 縁の点 (上の縁 = 波・横の揺れ / 下の縁 = 床・少しさざめく)
  const tx: number[] = [], ty: number[] = [], by: number[] = []
  const amp = sh.amp * (0.6 + 0.4 * Math.min(1, h / 60)) * (1 - 0.35 * pose.calm)
  for (let i = 0; i <= n; i += 1) {
    const x = x0 + ((x1 - x0) * i) / n
    const u = (x - g.xStart) / g.w
    const k = (2 * Math.PI) / sh.len, k2 = (2 * Math.PI) / sh.len2
    const wave = amp * Math.sin(k * x - sh.spd * t + g.seed) + sh.amp2 * Math.sin(k2 * x - sh.spd2 * t + g.seed * 2.3)
    const top = bottom - h * profile(u) + wave
    const sway = sh.sway * Math.min(1, h / 120) * Math.sin(sh.swaySpd * t + u * 2.4 + g.seed)
    tx.push(Math.max(g.xStart - V_BLEED, Math.min(g.xEnd + V_BLEED, x + sway)))
    ty.push(Math.min(bottom - 1, top))
    by.push(bottom + 1.5 * Math.sin(x * 0.11 + t * 4))
  }
  const topMin = Math.min(...ty)
  const outline = (): void => {
    ctx.beginPath()
    ctx.moveTo(x0, by[0]!)
    for (let i = 0; i <= n; i += 1) ctx.lineTo(tx[i]!, ty[i]!)
    for (let i = n; i >= 0; i -= 1) ctx.lineTo(x0 + ((x1 - x0) * i) / n, by[i]!)
    ctx.closePath()
  }
  // 地: 下は菫が濃く、中ほどは透けて、上の縁の辺りで銀藤がまた少し乗る (縁の波が読める)。上から薄れる。
  const topA = 1 - smooth01(0, 0.7, pose.fadeTop)
  const midA = 1 - smooth01(0.25, 0.9, pose.fadeTop)
  const gr = ctx.createLinearGradient(0, bottom, 0, topMin)
  gr.addColorStop(0, `rgba(${swc('unparry', '150,110,204')},${(0.55 * a).toFixed(3)})`)
  gr.addColorStop(0.35, `rgba(${swc('unparry', '170,140,220')},${(0.3 * a * midA).toFixed(3)})`)
  gr.addColorStop(0.7, `rgba(${sh.tint},${(0.2 * a * midA).toFixed(3)})`)
  gr.addColorStop(1, `rgba(${sh.tint},${(0.34 * a * topA).toFixed(3)})`)
  ctx.save()
  outline()
  ctx.fillStyle = gr
  ctx.fill()
  ctx.clip()
  // 襞: 縁の波の傾きで明るい襞・暗い襞を決め、細い紡錘形 (下は太く・上で細る) を重ねる。
  const foldStep = sh.len / 4
  const nf = Math.max(2, Math.floor(g.w / foldStep))
  for (let f = 0; f < nf; f += 1) {
    const i = Math.round(((f + 0.5) / nf) * n)
    const slope = (ty[Math.min(n, i + 1)]! - ty[Math.max(0, i - 1)]!) / (2 * STEP_PX)
    const lit = slope < 0
    const s = Math.min(1, Math.abs(slope) * 2.2)
    if (s < 0.08) continue
    const bx = x0 + ((x1 - x0) * i) / n
    const half = foldStep * (0.32 + 0.2 * hash01(g.seed + f))
    const fa = (lit ? 0.16 : 0.14) * s * a * (1 - 0.6 * pose.fadeTop)
    const col = lit ? sh.tint : '40,22,64'
    const hg = ctx.createLinearGradient(bx - half, 0, bx + half, 0)
    hg.addColorStop(0, `rgba(${col},0)`)
    hg.addColorStop(0.5, `rgba(${col},${fa.toFixed(3)})`)
    hg.addColorStop(1, `rgba(${col},0)`)
    ctx.fillStyle = hg
    ctx.beginPath()
    const lx = Math.max(g.xStart - V_BLEED, bx - half), rx = Math.min(g.xEnd + V_BLEED, bx + half)
    ctx.moveTo(lx, bottom + 2)
    ctx.quadraticCurveTo(lx + half * 0.2, (bottom + ty[i]!) / 2, tx[i]!, ty[i]! + 2)
    ctx.quadraticCurveTo(rx - half * 0.2, (bottom + ty[i]!) / 2, rx, bottom + 2)
    ctx.closePath()
    ctx.fill()
  }
  // 上の縁の照り: 縁に沿った薄い帯 (縁から下へ 7px・下へ向けて抜ける)。線では描かない。
  ctx.globalCompositeOperation = 'lighter'
  const hemA = 0.16 * a * topA
  if (hemA > 0.01) {
    for (const [d, al] of [[7, 0.5], [3.5, 0.5]] as const) {
      ctx.beginPath()
      ctx.moveTo(tx[0]!, ty[0]!)
      for (let i = 1; i <= n; i += 1) ctx.lineTo(tx[i]!, ty[i]!)
      for (let i = n; i >= 0; i -= 1) ctx.lineTo(tx[i]!, Math.min(bottom, ty[i]! + d))
      ctx.closePath()
      ctx.fillStyle = `rgba(${sh.tint},${(hemA * al).toFixed(3)})`
      ctx.fill()
    }
  }
  ctx.restore()
}

// 床の下からの菫の照り (弾けない合図・影の手と同じ色)。帯の中だけ。
function underGlow(ctx: CanvasRenderingContext2D, g: Geom, floorY: number, a: number, h: number): void {
  if (a <= 0.01) return
  ctx.save()
  ctx.globalCompositeOperation = 'lighter'
  ctx.beginPath()
  ctx.rect(g.xStart - V_BLEED, floorY - h, g.w + V_BLEED * 2, h)
  ctx.clip()
  ctx.translate(g.cx, floorY)
  ctx.scale((g.w / 2 + V_BLEED) / h, 1)
  const gr = ctx.createRadialGradient(0, 0, 0, 0, 0, h)
  gr.addColorStop(0, `rgba(${swc('unparry', '158,116,214')},${(0.42 * a).toFixed(3)})`)
  gr.addColorStop(0.6, `rgba(${swc('unparry', '150,110,204')},${(0.16 * a).toFixed(3)})`)
  gr.addColorStop(1, `rgba(${swc('unparry', '150,110,204')},0)`)
  ctx.fillStyle = gr
  ctx.fillRect(-h, -h, h * 2, h)
  ctx.restore()
}

function drawSheets(ctx: CanvasRenderingContext2D, kind: VeilClothKind, g: Geom, floorY: number, pose: Pose, timeSec: number): void {
  for (const sh of SHEETS[kind]) drawSheet(ctx, g, sh, floorY, pose, timeSec)
}

/** 予告 (telegraph)。裾が床に溜まってさざめき、最後に持ち上がってはためく。 */
export function drawVeilClothTelegraph(ctx: CanvasRenderingContext2D, kind: VeilClothKind, hz: ActiveHit, timeSec: number, floorY: number): void {
  const g = geomOf(hz)
  const p = clamp01(hz.elapsedMs / hz.telegraphMs)
  const late = smooth01(0.75, 1, p)
  const pulse = p >= 0.75 ? 1 + 0.18 * Math.sin(timeSec * 14) : 1
  underGlow(ctx, g, floorY, (0.35 + 0.55 * p) * pulse, 34 + 30 * p)
  // 裾: 最初の 30% で床に広がり (10px→)、26px まで溜まる。最後の 25% で約 40px へ。
  const pool = 10 + 16 * easeOut(p / 0.6)
  const h = (pool + (40 - pool) * late) * easeOut(p / 0.3)
  drawSheets(ctx, kind, g, floorY, { h, lift: 0, fadeTop: 0, fadeAll: 1 - smooth01(0, 0.3, p), calm: 0 }, timeSec)
}

/** 当たり (active)。 */
export function drawVeilClothActive(ctx: CanvasRenderingContext2D, kind: VeilClothKind, hz: ActiveHit, timeSec: number, floorY: number): void {
  const ms = hz.elapsedMs - hz.telegraphMs
  remember(kind, hz, timeSec - ms / 1000)
  drawClothAt(ctx, kind, geomOf(hz), floorY, ms, hz.activeMs, timeSec)
}

function drawClothAt(ctx: CanvasRenderingContext2D, kind: VeilClothKind, g: Geom, floorY: number, ms: number, activeMs: number, timeSec: number): void {
  const end = activeMs
  // 照り: 立ち上がりで強く、当たりの終わりで 120ms のうちに引く (危険が終わったと分かる)。
  const glowA = (ms < 120 ? 1.15 - ms / 800 : 1) * (1 - smooth01(end - 20, end + 120, ms))
  underGlow(ctx, g, floorY, glowA, 64)
  const rise = easeOutBack(ms / RISE_MS)
  const from = 40 / g.fullH
  const up = from + (1 - from) * rise
  // 垂れ: 当たりの終わりから、高さは 55% へ・床から 14px 浮き・上から薄れる。
  const q = clamp01((ms - end) / CLOTH_LINGER_MS)
  const sag = easeOut(q / 0.7)
  const h = g.fullH * up * (1 - 0.45 * sag)
  drawSheets(ctx, kind, g, floorY, { h, lift: 14 * sag, fadeTop: smooth01(0, 0.85, q), fadeAll: smooth01(0.55, 1, q), calm: sag }, timeSec)
}

// ---------------------------------------------------------------- 当たりの後の残り

type Trace = { kind: VeilClothKind; g: Geom; startSec: number; activeMs: number }
const traces = new Map<number, Trace>()
function remember(kind: VeilClothKind, hz: ActiveHit, startSec: number): void {
  if (!traces.has(hz.id)) traces.set(hz.id, { kind, g: geomOf(hz), startSec, activeMs: hz.activeMs })
}

/** 当たりが消えた後の残り (布が垂れて、上から薄れて消える)。render.ts の drawHazards の最後で呼ぶ。描くだけ。 */
export function drawVeilClothLinger(ctx: CanvasRenderingContext2D, timeSec: number, hits: readonly ActiveHit[], floorY: number): void {
  if (traces.size === 0) return
  for (const [id, tr] of traces) {
    const ms = (timeSec - tr.startSec) * 1000
    if (ms < 0 || ms > tr.activeMs + CLOTH_LINGER_MS) { traces.delete(id); continue }
    if (hits.some((h) => h.id === id)) continue   // まだ当たりの中 = 本体側で描いている
    drawClothAt(ctx, tr.kind, tr.g, floorY, ms, tr.activeMs, timeSec)
  }
}
